import React, { FC, useMemo, useCallback, MouseEvent, useState } from 'react';
import { Avatar, Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
	EventUserRolesEnum,
	FileVariantsEnum,
	getEventPriceRangeString,
	getFreeSpotsCount,
	getSpotsLeftText,
	isNoSpotsLeft,
	formatEventTimeRange,
	priceFormatter,
	UTMTagsBuilder,
	UTMSourceEnum,
	UTMTagsUtils,
} from '@sweatpals/shared';
import { useRouter } from 'next/router';
import Image from 'next/legacy/image';
import { addHours, isBefore } from 'date-fns';
import { random } from 'lodash-es';
import { eventCardStyles } from './event-card.styles';
import { getPublicFileUrl } from '@/modules/_core/utils/file.utils';
import { CalendarIcon, LocationIcon, MoneyIcon } from '@/modules/_core/icons';
import LocationAddressLink from '@/modules/_core/components/links/LocationAddressLink';
import { EventType } from '@/modules/_core/types/event-types';
import useLink from '@/modules/_core/hooks/useLink';
import RemoteImage from '@/modules/_core/components/images/Remote-image';
import { getDarken, getLighten } from '@/modules/_core/utils/color';
import { UserDataType } from '@/modules/_core/types/user-types';
import { eventParticipantsPlaceholders } from '@/modules/_core/constants/event-card-participants';
import { defaultHostColorHex } from '../../pages/host/host-page.styles';
import { FadeSpinner } from '@/modules/_core/components/loaders';
import { Showable, useStyles } from '@sweatpals/react-ui-kit';
import { CoreImage } from '@/modules/_core/components/_ui/core-image';

interface EventCardProps {
	event: EventType;
	user?: UserDataType;
	primaryColor?: string;
	onHostLinkClick?: (eventAlias: string, shortLocalInstance?: string) => void;
}

const EventCard: FC<EventCardProps> = (props) => {
	const {
		event,
		user,
		primaryColor = defaultHostColorHex,
		onHostLinkClick,
	} = props;
	const [isOpeningEventPage, setIsOpeningEventPage] = useState(false);

	const backgroundColor = useMemo(() => getDarken(primaryColor, 1), [primaryColor]);
	const lightTextColor = useMemo(() => getLighten(primaryColor, 1), [primaryColor]);
	const hoverColor = useMemo(() => getDarken(primaryColor, 0.1), [primaryColor]);

	const styles = useStyles(eventCardStyles, primaryColor, backgroundColor, hoverColor, lightTextColor, isOpeningEventPage);
	const theme = useTheme();
	const { makeEventLink } = useLink();
	const router = useRouter();

	const participants = useMemo(() => event.participants?.slice(0, 3) ?? [], [event]);
	const participantsCount = useMemo(() => event.participantsCount ?? 0, [event.participantsCount]);
	const isHost = useMemo(() => event.author && user && event.author.id === user.id, [event.author, user]);
	const isCoHost = useMemo(() => event.coHost && user && event.coHost.id === user.id, [event.coHost, user]);
	const isRsvpListHidden = useMemo(() => (event.isRsvpListHidden && !isHost && !isCoHost), [event.isRsvpListHidden, isHost, isCoHost]);
	const eventPrice = useMemo(
		() => (event.isPaid ? event.isDonationBased ? `Donation-based ${event.minimalDonationAmount > 0 ? `(minimum ${priceFormatter(event.minimalDonationAmount)})` : 0}` : getEventPriceRangeString(event.prices) : 'Free'),
		[event.isPaid, event.isDonationBased, event.prices]
	);
	const isLessThanSm = useMediaQuery(theme.breakpoints.down('sm'));
	const isEventSoldOut = useMemo(
		() => event.attendeesLimit && isNoSpotsLeft(event.attendeesLimit, participantsCount, true, !!event.coHost),
		[event.attendeesLimit, participantsCount, event.coHost]
	);
	const spotsLeftText = useMemo(() => {
		if (!event.attendeesLimit) {
			return;
		}

		const freeSpotsCount = getFreeSpotsCount(event.attendeesLimit, participantsCount, 0, true, !!event.coHost);

		return getSpotsLeftText(freeSpotsCount, event.attendeesLimit);
	}, [event.attendeesLimit, participantsCount, event.coHost]);
	const isPastEvent = useMemo(() => (event.endDate
		? isBefore(new Date(event.endDate), new Date())
		: isBefore(addHours(new Date(event.startDate), 2), new Date())
	), [event.endDate, event.startDate]);

	const openEventPage = useCallback(async (isCheckout?: boolean): Promise<void> => {
		setIsOpeningEventPage(true);

		try {
			const eventAlias = event.alias;
			const shortLocalInstance = event.calculated_is_serial || isPastEvent ? event.shortLocalInstance : undefined;

			onHostLinkClick?.(eventAlias, shortLocalInstance);
			const eventLink = makeEventLink({
				eventAlias,
				shortLocalInstance,
				isCheckout,
			});

			const savedUtmTags = sessionStorage.getItem('utmTags');

			if (savedUtmTags) {
				await router.push(eventLink);
				return;
			}

			const tags = UTMTagsBuilder.create()
				.setUtmSource(UTMSourceEnum.Web)
				.setUtmCampaign('web_community_page')
				.setUtmOwner(event.authorId)
				.setUtmAdset(event.id)
				.build();

			await router.push(UTMTagsUtils.appendQueryString(eventLink, tags));

		} finally {
			setIsOpeningEventPage(true);
		}
	}, [event.alias, event.shortLocalInstance, isPastEvent, makeEventLink, router]);

	const joinToEvent = useCallback(async (e: MouseEvent<HTMLButtonElement>): Promise<void> => {
		e.stopPropagation();
		openEventPage(true);
	}, [openEventPage]);

	const eventParticipantsPlaceholder = useMemo(
		() => eventParticipantsPlaceholders[random(0, eventParticipantsPlaceholders.length - 1)],
		[]
	);

	return (
		<Box
			sx={styles.root}
			onClick={() => {
				if (!isOpeningEventPage) {
					openEventPage();
				}
			}}
		>
			<Showable show={isOpeningEventPage}>
				<Box sx={styles.loader}>
					<FadeSpinner size={60} color="white" />
				</Box>
			</Showable>
			<CoreImage
				src={event.background ? getPublicFileUrl(event.background, { variant: FileVariantsEnum.M }) : ''}
				blurDataURL={event.background && getPublicFileUrl(event.background, { variant: FileVariantsEnum.XS })}
				wrapperSx={styles.avatar}
				style={{
					backgroundRepeat: 'no-repeat',
					backgroundPosition: 'center',
					backgroundSize: 'cover',
				}}
			/>
			<Box sx={{ ...styles.content, gap: { xs: spotsLeftText ? '26px' : '16px', sm: '16px' } }}>
				<Showable show={isLessThanSm}>
					<Box sx={styles.price}>
						{eventPrice}
					</Box>
				</Showable>
				<Box sx={styles.data}>
					<Box sx={styles.name}>{event.name}</Box>
					<Box display="flex" flexDirection="column" gap="10px">
						<Showable show={!event.additionalData?.hideEventDate}>
							<Box sx={styles.item}>
								<CalendarIcon />
								<Typography sx={styles.itemText} suppressHydrationWarning>
									{formatEventTimeRange(event.startDate, event.endDate, {
										timezone: event.tzid,
										startDateFormat: 'eee MMM do, h:mm aa',
										endDateFormat: 'eee MMM do, h:mm aa',
									})}
								</Typography>
							</Box>
						</Showable>
						<Showable hide={isLessThanSm}>
							<Box sx={styles.item}>
								<LocationIcon />
								<Typography sx={styles.itemText}>
									{event.isOnlineEvent ? 'Online event' : <LocationAddressLink address={event.addressName} />}
								</Typography>
							</Box>
							<Box sx={styles.item}>
								<MoneyIcon />
								<Typography sx={styles.itemText}>
									{eventPrice}
								</Typography>
							</Box>
						</Showable>
					</Box>
				</Box>
				<Box
					display="flex"
					flexDirection={{ xs: 'row', sm: 'column' }}
					justifyContent={{ xs: 'space-between', sm: 'center' }}
					alignItems={{ xs: 'center', sm: 'unset' }}
					gap="16px"
				>
					<Showable show={!isPastEvent}>
						<Box position="relative">
							<Showable show={spotsLeftText}>
								<Typography sx={styles.spotsLeftText}>{spotsLeftText}</Typography>
							</Showable>
							<Button
								disabled={isEventSoldOut}
								sx={styles.joinButton}
								onClick={joinToEvent}
							>
								Get ticket
							</Button>
						</Box>
					</Showable>
					<Showable show={isPastEvent}>
						<Box display="flex" flexDirection="column" gap="8px" alignItems="center">
							<Typography sx={styles.pastEventLabel}>This event has happened</Typography>
							<Showable show={event.calculated_is_serial}>
								<Button
									disabled={isEventSoldOut}
									sx={styles.viewOtherDates}
									onClick={(e) => {
										e.stopPropagation();
										openEventPage();
									}}
								>
									view other dates
								</Button>
							</Showable>
						</Box>
					</Showable>
					<Showable hide={isRsvpListHidden}>
						<Box sx={styles.avatarGroup}>
							{participants.map(({ id, user }) => (user ? (
								<Avatar
									key={id}
									src={user.avatarId && getPublicFileUrl(user.avatarId, {
										variant: FileVariantsEnum.S
									})}
									alt={user.fullName}
									title={user.fullName}
								>
									<RemoteImage
										alt={user.fullName}
										src={null}
										width={24}
										height={24}
									/>
								</Avatar>
							) : null))}
							{participantsCount > 3 ? (
								<Box sx={styles.participantsCount}>
									<Typography>+{participantsCount - 3}</Typography>
								</Box>
							) : null}
						</Box>
					</Showable>
					<Showable show={isRsvpListHidden}>
						<Box sx={{ height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<Image
								src={eventParticipantsPlaceholder}
								alt="event-participants-placeholder"
							/>
						</Box>
					</Showable>
				</Box>
			</Box>
		</Box>
	);
};

export default EventCard;
